import { Phone, Star, ChevronDown, Video, X, Menu } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isRegionsOpen, setIsRegionsOpen] = useState(false);
  const [showBesichtigungPopup, setShowBesichtigungPopup] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isMobileRegionsOpen, setIsMobileRegionsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsMobileServicesOpen(false);
    setIsMobileRegionsOpen(false);
    if (path === '/') {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const scrollToSection = (id: string) => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLogoClick = () => {
    navigate('/');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 bg-slate-800 text-white py-2.5">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-6 text-sm">
            <div className="flex items-center space-x-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="font-semibold">4.9/5</span>
            </div>
            <div className="hidden md:block text-slate-300">|</div>
            <div className="hidden md:block">
              <span className="text-slate-300">Über</span> <span className="font-semibold">500+ zufriedene Kunden</span>
            </div>
            <div className="hidden md:block text-slate-300">|</div>
            <div className="hidden md:block">
              <span className="font-semibold">Gratis Besichtigung</span> <span className="text-slate-300">& Beratung</span>
            </div>
          </div>
        </div>
      </div>
      <header
        className={`fixed top-[40px] left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md py-3' : 'bg-white/95 backdrop-blur-sm py-4'
        }`}
      >
      <div className="max-w-[1920px] mx-auto px-8">
        <div className="flex items-center justify-between w-full">
          <button onClick={handleLogoClick} className="flex items-center space-x-2 hover:opacity-80 transition">
            <img
              src="/Logo_geographie_suisse_avec_cercle_noir.svg copy.png"
              alt="umzug-schweiz-deutschland.ch Logo"
              className="w-10 h-10 object-contain"
            />
            <div>
              <div className="font-bold text-slate-800 text-lg leading-tight">
                umzug-schweiz-deutschland.ch
              </div>
              <div className="text-xs text-slate-600">Ihr Partner für Grenzumzüge</div>
            </div>
          </button>

          <nav className="hidden lg:flex items-center space-x-10 ml-20">
            <button onClick={handleLogoClick} className="text-slate-700 hover:text-slate-900 font-medium transition">
              Start
            </button>
            <button onClick={() => handleNavigation('/leistungen')} className="text-slate-700 hover:text-slate-900 font-medium transition">
              Leistungen
            </button>
            <button onClick={() => handleNavigation('/preise')} className="text-slate-700 hover:text-slate-900 font-medium transition">
              Preise
            </button>
            <button onClick={() => handleNavigation('/ablauf')} className="text-slate-700 hover:text-slate-900 font-medium transition">
              Ablauf
            </button>
            <button onClick={() => handleNavigation('/faq')} className="text-slate-700 hover:text-slate-900 font-medium transition">
              FAQ
            </button>
            <button onClick={() => handleNavigation('/kontakt')} className="text-slate-700 hover:text-slate-900 font-medium transition">
              Kontakt
            </button>
            <div
              className="relative group"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button className="text-slate-700 hover:text-slate-900 font-medium transition flex items-center space-x-1">
                <span>Unsere Services</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {isServicesOpen && (
                <div
                  className="absolute top-full left-0 pt-2 w-80 z-50"
                  onMouseEnter={() => setIsServicesOpen(true)}
                  onMouseLeave={() => setIsServicesOpen(false)}
                >
                  <div className="bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                  <button
                    onClick={() => { handleNavigation('/komplettumzug'); setIsServicesOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Komplettumzug
                  </button>
                  <button
                    onClick={() => { handleNavigation('/teilumzug'); setIsServicesOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Teilumzug
                  </button>
                  <button
                    onClick={() => { handleNavigation('/firmenumzuege'); setIsServicesOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Firmenumzüge
                  </button>
                  <button
                    onClick={() => { handleNavigation('/express-umzuege'); setIsServicesOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Express-Umzüge
                  </button>
                  <button
                    onClick={() => { handleNavigation('/zollabwicklung'); setIsServicesOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Zollabwicklung
                  </button>
                  <button
                    onClick={() => { handleNavigation('/zwischenlagerung'); setIsServicesOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Zwischenlagerung
                  </button>
                  <button
                    onClick={() => { handleNavigation('/verpackungsmaterial'); setIsServicesOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Verpackungsmaterial
                  </button>
                  <button
                    onClick={() => { handleNavigation('/endreinigung'); setIsServicesOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Endreinigung
                  </button>
                  <button
                    onClick={() => { handleNavigation('/sofort-besichtigung'); setIsServicesOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Sofort Besichtigung
                  </button>
                  </div>
                </div>
              )}
            </div>
            <div
              className="relative group"
              onMouseEnter={() => setIsRegionsOpen(true)}
              onMouseLeave={() => setIsRegionsOpen(false)}
            >
              <button className="text-slate-700 hover:text-slate-900 font-medium transition flex items-center space-x-1">
                <span>Regionen</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isRegionsOpen ? 'rotate-180' : ''}`} />
              </button>
              {isRegionsOpen && (
                <div
                  className="absolute top-full left-0 pt-2 w-80 z-50"
                  onMouseEnter={() => setIsRegionsOpen(true)}
                  onMouseLeave={() => setIsRegionsOpen(false)}
                >
                  <div className="bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                  <button
                    onClick={() => { handleNavigation('/umzug-zuerich-deutschland'); setIsRegionsOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Umzug Zürich Deutschland
                  </button>
                  <button
                    onClick={() => { handleNavigation('/umzug-basel-deutschland'); setIsRegionsOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Umzug Basel Deutschland
                  </button>
                  <button
                    onClick={() => { handleNavigation('/umzug-bern-deutschland'); setIsRegionsOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Umzug Bern Deutschland
                  </button>
                  <button
                    onClick={() => { handleNavigation('/umzug-luzern-deutschland'); setIsRegionsOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Umzug Luzern Deutschland
                  </button>
                  <button
                    onClick={() => { handleNavigation('/umzug-aargau-deutschland'); setIsRegionsOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Umzug Aargau Deutschland
                  </button>
                  <button
                    onClick={() => { handleNavigation('/umzug-winterthur-deutschland'); setIsRegionsOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Umzug Winterthur Deutschland
                  </button>
                  <button
                    onClick={() => { handleNavigation('/umzug-stgallen-deutschland'); setIsRegionsOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Umzug St. Gallen Deutschland
                  </button>
                  <button
                    onClick={() => { handleNavigation('/umzug-zug-deutschland'); setIsRegionsOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition font-medium"
                  >
                    Umzug Zug Deutschland
                  </button>
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center space-x-4">
            <a
              href="tel:0800700100"
              className="hidden sm:flex items-center space-x-2 text-green-600 hover:text-green-700 transition"
            >
              <Phone className="w-4 h-4" />
              <span className="font-semibold">0800 700 100</span>
            </a>
            <button
              onClick={() => setShowBesichtigungPopup(true)}
              className="hidden lg:flex bg-green-700 hover:bg-green-800 text-white px-5 py-3 rounded-lg font-semibold transition shadow-sm hover:shadow-md items-center space-x-2 min-h-[44px]"
              aria-label="Sofort Besichtigung anfordern"
            >
              <Video className="w-4 h-4" />
              <span>Sofort Besichtigung</span>
            </button>
            <button
              onClick={() => handleNavigation('/offerte-anfordern')}
              className="hidden lg:block bg-slate-800 hover:bg-slate-900 text-white px-5 py-3 rounded-lg font-semibold transition shadow-sm hover:shadow-md min-h-[44px]"
              aria-label="Kostenlose Offerte anfordern"
            >
              Offerte anfordern
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden bg-slate-800 hover:bg-slate-900 text-white p-3 rounded-lg transition min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Menu öffnen"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>
      </header>

      {isMobileMenuOpen && (
        <div className="fixed top-[112px] left-0 right-0 bottom-0 z-[100] lg:hidden bg-white overflow-y-auto shadow-lg">
          <div className="px-4 py-4">
              <nav className="space-y-2">
                <button
                  onClick={() => handleNavigation('/')}
                  className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition"
                >
                  Start
                </button>

                <button
                  onClick={() => handleNavigation('/leistungen')}
                  className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition"
                >
                  Leistungen
                </button>

                <button
                  onClick={() => handleNavigation('/preise')}
                  className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition"
                >
                  Preise
                </button>

                <button
                  onClick={() => handleNavigation('/ablauf')}
                  className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition"
                >
                  Ablauf
                </button>

                <button
                  onClick={() => handleNavigation('/faq')}
                  className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition"
                >
                  FAQ
                </button>

                <button
                  onClick={() => handleNavigation('/kontakt')}
                  className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition"
                >
                  Kontakt
                </button>

                <div className="border-t border-slate-200 my-2" />

                <button
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition flex items-center justify-between"
                >
                  <span>Unsere Services</span>
                  <ChevronDown className={`w-5 h-5 transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>

                {isMobileServicesOpen && (
                  <div className="pl-4 space-y-1">
                    <button
                      onClick={() => handleNavigation('/komplettumzug')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Komplettumzug
                    </button>
                    <button
                      onClick={() => handleNavigation('/teilumzug')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Teilumzug
                    </button>
                    <button
                      onClick={() => handleNavigation('/firmenumzuege')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Firmenumzüge
                    </button>
                    <button
                      onClick={() => handleNavigation('/express-umzuege')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Express-Umzüge
                    </button>
                    <button
                      onClick={() => handleNavigation('/zollabwicklung')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Zollabwicklung
                    </button>
                    <button
                      onClick={() => handleNavigation('/zwischenlagerung')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Zwischenlagerung
                    </button>
                    <button
                      onClick={() => handleNavigation('/verpackungsmaterial')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Verpackungsmaterial
                    </button>
                    <button
                      onClick={() => handleNavigation('/endreinigung')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Endreinigung
                    </button>
                    <button
                      onClick={() => handleNavigation('/sofort-besichtigung')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Sofort Besichtigung
                    </button>
                  </div>
                )}

                <button
                  onClick={() => setIsMobileRegionsOpen(!isMobileRegionsOpen)}
                  className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition flex items-center justify-between"
                >
                  <span>Regionen</span>
                  <ChevronDown className={`w-5 h-5 transition-transform ${isMobileRegionsOpen ? 'rotate-180' : ''}`} />
                </button>

                {isMobileRegionsOpen && (
                  <div className="pl-4 space-y-1">
                    <button
                      onClick={() => handleNavigation('/umzug-zuerich-deutschland')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Umzug Zürich Deutschland
                    </button>
                    <button
                      onClick={() => handleNavigation('/umzug-basel-deutschland')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Umzug Basel Deutschland
                    </button>
                    <button
                      onClick={() => handleNavigation('/umzug-bern-deutschland')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Umzug Bern Deutschland
                    </button>
                    <button
                      onClick={() => handleNavigation('/umzug-luzern-deutschland')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Umzug Luzern Deutschland
                    </button>
                    <button
                      onClick={() => handleNavigation('/umzug-aargau-deutschland')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Umzug Aargau Deutschland
                    </button>
                    <button
                      onClick={() => handleNavigation('/umzug-winterthur-deutschland')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Umzug Winterthur Deutschland
                    </button>
                    <button
                      onClick={() => handleNavigation('/umzug-stgallen-deutschland')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Umzug St. Gallen Deutschland
                    </button>
                    <button
                      onClick={() => handleNavigation('/umzug-zug-deutschland')}
                      className="w-full text-left px-4 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg transition"
                    >
                      Umzug Zug Deutschland
                    </button>
                  </div>
                )}

                <div className="border-t border-slate-200 my-4" />

                <div className="space-y-3 px-4">
                  <button
                    onClick={() => {
                      setShowBesichtigungPopup(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-green-700 hover:bg-green-800 text-white px-5 py-3 rounded-lg font-semibold transition shadow-sm hover:shadow-md flex items-center justify-center space-x-2"
                  >
                    <Video className="w-5 h-5" />
                    <span>Sofort Besichtigung</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/offerte-anfordern')}
                    className="w-full bg-slate-800 hover:bg-slate-900 text-white px-5 py-3 rounded-lg font-semibold transition shadow-sm hover:shadow-md"
                  >
                    Offerte anfordern
                  </button>
                  <a
                    href="tel:0800700100"
                    className="w-full flex items-center justify-center space-x-2 text-green-600 hover:text-green-700 transition border-2 border-green-600 hover:border-green-700 px-5 py-3 rounded-lg font-semibold"
                  >
                    <Phone className="w-5 h-5" />
                    <span>0800 700 100</span>
                  </a>
                </div>
              </nav>
          </div>
        </div>
      )}

      {showBesichtigungPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 relative animate-scale-in">
            <button
              onClick={() => setShowBesichtigungPopup(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Video className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                Zeit für eine spontane Besichtigung?
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Wir sind jetzt online und können Sie gleich bedienen!
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3 bg-green-50 rounded-lg p-3">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-green-700">Online & verfügbar</span>
              </div>
              <div className="text-sm text-slate-600 space-y-2">
                <p>✓ Kostenlose Video-Besichtigung</p>
                <p>✓ Sofortiges Festpreis-Angebot</p>
                <p>✓ Keine Wartezeit</p>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowBesichtigungPopup(false);
                  handleNavigation('/sofort-besichtigung');
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition shadow-md hover:shadow-lg"
              >
                Jetzt Video-Termin buchen
              </button>
              <button
                onClick={() => setShowBesichtigungPopup(false)}
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-3 rounded-lg font-semibold transition"
              >
                Vielleicht später
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
